/**
 * One automation, opened — the workspace (D9, D10, D11b). Same component on every width.
 *
 * ONE IMPLEMENTATION, TWO PLACEMENTS, as before. Beside (or under) the rail on
 * a wide screen, inside the bottom sheet on a phone. The component knows
 * neither: it takes a detail object and renders it, so the sheet cannot drift
 * into a second, mobile-only contract.
 *
 * HEADER, THEN THE TABS. The header (`AutomationWorkspaceHeader`) holds what
 * is true on every tab — name, category, description, the status word and the
 * switch. Below it: Allgemein, Auslöser, Ausführungen, Protokoll, and for an
 * automation that sends e-mail "E-Mail-Vorlage" as the second
 * (`workspaceTabsFor`). The move-in address has no template tab and no panel
 * for one — not a hidden one, none.
 *
 * THREE TABS COST REQUESTS AND ARE `lazyMount`. Ausführungen reads the run
 * history, Protokoll the activity timeline (both gated on the active tab by the
 * page), and E-Mail-Vorlage its template (gated by `templateActive`). No panel
 * exists before its tab is opened. Allgemein and Auslöser are read off the
 * detail already in hand.
 *
 * THE TEMPLATE PANEL IS KEYED BY THE AUTOMATION. Choosing another automation
 * mounts a fresh template workspace: a draft, a preview or a late answer of the
 * previous one cannot appear under the next one's name.
 *
 * MANUAL ACTIVATION. The active tab is URL state: every activation is a history
 * entry, and two of the four fetch. With Radix's default, an arrow key both
 * moves focus AND activates, so arrowing across the tab bar would push one
 * history entry and fire one request per key press. With `manual`, arrows move
 * focus and Enter/Space activate — the tab pattern WAI-ARIA recommends when
 * showing a panel is not instant.
 *
 * A RUN IS OPENED, NOT SHOWN, HERE (D11b). A row of Ausführungen reports which
 * run was chosen (`onOpenRun`); the detail panel is the page's, because the
 * address decides it and only the page reads the address.
 *
 * THE WRITE PASSES STRAIGHT THROUGH. `onAutomationUpdated` goes to the header's
 * switch and is not called here: the answer has to reach the list row and the
 * detail, which only the page can see. The same for `onTestEmailAttempted`
 * (TEST-EMAIL T3): the template tab reports a test send the server may have
 * recorded, and only the page holds the Protokoll it belongs in.
 *
 * THE ACTIVE TAB IS ALWAYS IN VIEW (Final QA). On a phone the five tabs are
 * wider than the screen and the bar scrolls sideways; opened on Protokoll by a
 * link, the bar used to start at Allgemein with the active tab out of sight.
 * Whenever the active tab, the automation or the loading state changes, the
 * bar's OWN horizontal offset moves by the least amount that shows it. Nothing
 * else moves: not the page, not the sheet, not the address — which is why it
 * is not `scrollIntoView`, which also scrolls every scrollable ancestor.
 *
 * THE TAB BAR IS DRAWN FROM THEME TOKENS (`accent="primary"`, Final QA): the
 * active tab in the primary colour in both themes, the others in the muted
 * text colour (AA in both), the focus ring inside the tab so the scrolling
 * bar cannot clip it. The shared component's other accents are unchanged.
 */
import React, { useEffect, useRef } from "react";

import { DetailTabs, DetailTabPanel } from "../../../components/ui/DetailTabs";
import AutomationGeneralTab from "./AutomationGeneralTab";
import AutomationProtocolTab from "./AutomationProtocolTab";
import AutomationRunsTab from "./AutomationRunsTab";
import AutomationTemplateTab from "./AutomationTemplateTab";
import AutomationTriggerTab from "./AutomationTriggerTab";
import AutomationWorkspaceHeader from "./AutomationWorkspaceHeader";
import {
  EXECUTIONS_TAB,
  GENERAL_TAB,
  PROTOCOL_TAB,
  TEMPLATE_TAB,
  TRIGGER_TAB,
  hasTemplateWorkspace,
  workspaceTabsFor,
} from "./automationWorkspaceTabs";

const RETRY_CLASS =
  "rounded-[9px] border border-border bg-card px-3 py-1.5 text-[12px] font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-md:min-h-9";

// Shows `tab` inside the horizontally scrolling `list` by the least movement
// ("nearest"): a tab already in view leaves the offset alone.
function revealTab(list, tab) {
  const bar = list.getBoundingClientRect();
  const box = tab.getBoundingClientRect();
  if (box.left < bar.left) list.scrollLeft -= bar.left - box.left;
  else if (box.right > bar.right) list.scrollLeft += box.right - bar.right;
}

export default function AutomationWorkspace({
  isMobile,
  detail,
  loading,
  error,
  onRetry,
  activeTab,
  onTabChange,
  runs,
  activity,
  templateActive = false,
  onAutomationUpdated,
  onTestEmailAttempted,
  openRunId = null,
  onOpenRun,
  onRefreshRuns,
}) {
  // The workspace's own tab bar is the first tab list in the section: the
  // header has none and every panel comes after the bar.
  const workspaceRef = useRef(null);
  const detailKey = detail ? detail.key : null;
  useEffect(() => {
    const list = workspaceRef.current?.querySelector('[role="tablist"]');
    const tab = list?.querySelector('[role="tab"][data-state="active"]');
    if (list && tab) revealTab(list, tab);
  }, [activeTab, detailKey, loading, error]);

  if (loading) {
    return (
      <p className="m-0 text-[12.5px] text-muted-foreground">
        Automatisierung wird geladen …
      </p>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="m-0 text-[12.5px] text-destructive">
          {error}
        </p>
        <button type="button" onClick={onRetry} className={RETRY_CLASS}>
          Erneut versuchen
        </button>
      </div>
    );
  }

  if (!detail) {
    return (
      <p className="m-0 text-[12.5px] text-muted-foreground">
        Automatisierung auswählen.
      </p>
    );
  }

  return (
    // A named region, so "the workspace" is something a reader can jump to and
    // a test can scope to by name. The name is unchanged since A4.
    <section
      ref={workspaceRef}
      aria-label="Automatisierungsdetail"
      className="flex min-w-0 flex-col gap-5"
    >
      <AutomationWorkspaceHeader
        automation={detail}
        onUpdated={onAutomationUpdated}
      />

      <DetailTabs
        tabs={workspaceTabsFor(detail)}
        value={activeTab}
        onValueChange={onTabChange}
        accent="primary"
        activationMode="manual"
      >
        <DetailTabPanel tabId={GENERAL_TAB}>
          <AutomationGeneralTab automation={detail} />
        </DetailTabPanel>
        {hasTemplateWorkspace(detail) ? (
          <DetailTabPanel tabId={TEMPLATE_TAB}>
            <AutomationTemplateTab
              key={detail.key}
              automation={detail}
              active={templateActive}
              onTestEmailAttempted={onTestEmailAttempted}
            />
          </DetailTabPanel>
        ) : null}
        <DetailTabPanel tabId={TRIGGER_TAB}>
          <AutomationTriggerTab automation={detail} />
        </DetailTabPanel>
        <DetailTabPanel tabId={EXECUTIONS_TAB}>
          <AutomationRunsTab
            isMobile={isMobile}
            actionType={detail.action_type}
            items={runs.items}
            total={runs.total}
            limit={runs.limit}
            offset={runs.offset}
            loading={runs.loading}
            error={runs.error}
            onRetry={runs.reload}
            onRefresh={onRefreshRuns}
            onOffsetChange={runs.setOffset}
            openRunId={openRunId}
            onOpenRun={onOpenRun}
          />
        </DetailTabPanel>
        <DetailTabPanel tabId={PROTOCOL_TAB}>
          <AutomationProtocolTab
            items={activity.items}
            total={activity.total}
            loading={activity.loading}
            error={activity.error}
            onRetry={activity.reload}
          />
        </DetailTabPanel>
      </DetailTabs>
    </section>
  );
}
